// useEventFilters.tsx - Updated version with sorting
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import { EventFilters, EventSortConfig } from './types';

import { DetectedEvent } from '@ais-ui/websockets';
import { CombinedGroundingEventAndConfiguration } from './Detectors/Grounding/GroundingEvent';
import { usePermissions } from '../Permissions/PermissionsProvider';

// Severity order for sorting (lowest to highest priority)
const SEVERITY_ORDER: Record<string, number> = {
  all: 0,
  note: 1,
  warning: 2,
  alert: 3,
};

export const useEventFilters = (
  events: DetectedEvent[] | null | undefined,
  groundingEvents: CombinedGroundingEventAndConfiguration[] | null | undefined,
  search: string
) => {
  const permissions = usePermissions();

  const debouncedSearch = useDebounce(
    search.length >= 3 ? search : '',
    200,
    ''
  );

  const [eventFilters, setEventFilters] = useState<EventFilters>({
    severity: 'all',
  });

  const [sortConfig, setSortConfig] = useState<EventSortConfig>({
    type: 'date',
    direction: 'desc', // Most recent first by default
  });

  const filterEvents = useCallback(
    (events: DetectedEvent[] | undefined, searchTerm: string) => {
      if (!events) return [];

      return events.filter((event) => {
        return (
          event.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.shipName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false // Defensive programming - if fields are missing, don't crash
        );
      });
    },
    []
  );

  // Helper function to filter grounding events by search term
  const filterGroundingEvents = useCallback(
    (
      events: CombinedGroundingEventAndConfiguration[] | undefined,
      searchTerm: string
    ) => {
      if (!events || !searchTerm.trim()) return events || [];

      const normalizedSearchTerm = searchTerm.toLowerCase().trim();

      return events.filter((event) => {
        // Search in vessel name
        const vesselNameMatch = event.event?.vesselName
          ?.toLowerCase()
          .includes(normalizedSearchTerm);

        // Search in detector/configuration name
        const detectorNameMatch = event.configuration?.name
          ?.toLowerCase()
          .includes(normalizedSearchTerm);

        // Search in event UUID (could be considered as event name/identifier)
        const eventIdMatch = event.event?.uuid
          ?.toLowerCase()
          .includes(normalizedSearchTerm);

        // Search in MMSI (as string)
        const mmsiMatch = event.event?.mmsi
          ?.toString()
          .includes(normalizedSearchTerm);

        // Search in call sign
        const callSignMatch = event.event?.callSign
          ?.toLowerCase()
          .includes(normalizedSearchTerm);

        // Search in IMO (as string)
        const imoMatch = event.event?.imo
          ?.toString()
          .includes(normalizedSearchTerm);

        return (
          vesselNameMatch ||
          detectorNameMatch ||
          eventIdMatch ||
          mmsiMatch ||
          callSignMatch ||
          imoMatch
        );
      });
    },
    []
  );

  // Filter events by severity
  const filterBySeverity = useCallback(
    (
      eventsToBeFiltered: DetectedEvent[],
      severityFilter: string
    ): DetectedEvent[] => {
      if (severityFilter === 'all') {
        return eventsToBeFiltered;
      }

      return eventsToBeFiltered.filter((event) => {
        // Filter based on event's severity - defensive check
        return event.severity?.toLowerCase().includes(severityFilter) || false;
      });
    },
    []
  );

  // Filter grounding events by severity
  const filterGroundingEventsBySeverity = useCallback(
    (
      eventsToBeFiltered: CombinedGroundingEventAndConfiguration[],
      severityFilter: string
    ): CombinedGroundingEventAndConfiguration[] => {
      if (severityFilter === 'all') {
        return eventsToBeFiltered;
      }
      // Grounding events don't have a severity property
      return eventsToBeFiltered;
    },
    []
  );

  // Sort combined events
  const sortEvents = useCallback(
    (eventsToSort: any[]) => {
      const sorted = [...eventsToSort].sort((a, b) => {
        let comparison = 0;

        if (sortConfig.type === 'date') {
          // Sort by timestamp
          const timestampA = new Date(a.timestamp || 0).getTime();
          const timestampB = new Date(b.timestamp || 0).getTime();
          comparison = timestampA - timestampB;
        } else if (sortConfig.type === 'severity') {
          // Get severity values with defensive defaults
          const severityA =
            a.type === 'regular'
              ? a.event?.severity?.toLowerCase() || 'note'
              : 'note'; // Grounding events don't have severity
          const severityB =
            b.type === 'regular'
              ? b.event?.severity?.toLowerCase() || 'note'
              : 'note';

          // Use severity order mapping
          const orderA = SEVERITY_ORDER[severityA] ?? 0;
          const orderB = SEVERITY_ORDER[severityB] ?? 0;
          comparison = orderA - orderB;

          // If severities are equal, sort by date as secondary sort
          if (comparison === 0) {
            const timestampA = new Date(a.timestamp || 0).getTime();
            const timestampB = new Date(b.timestamp || 0).getTime();
            comparison = timestampA - timestampB;
          }
        }

        // Apply sort direction
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });

      return sorted;
    },
    [sortConfig]
  );

  // Filter regular events with search and severity
  const filteredRegularEvents = useMemo(() => {
    const eventsToProcess = events || [];

    // Apply search filter
    const searchFiltered = debouncedSearch
      ? filterEvents(eventsToProcess, debouncedSearch)
      : eventsToProcess;

    // Apply severity filter
    return filterBySeverity(searchFiltered, eventFilters.severity);
  }, [
    events,
    debouncedSearch,
    eventFilters.severity,
    filterEvents,
    filterBySeverity,
  ]);

  // Filter grounding events with search and severity
  const filteredGroundingEvents = useMemo(() => {
    // Handle permissions
    if (!permissions.grounding) {
      return [];
    }

    const eventsToProcess = groundingEvents || [];

    // Apply search filter
    const searchFiltered = debouncedSearch
      ? filterGroundingEvents(eventsToProcess, debouncedSearch)
      : eventsToProcess;

    // Apply severity filter
    return filterGroundingEventsBySeverity(
      searchFiltered,
      eventFilters.severity
    );
  }, [
    groundingEvents,
    debouncedSearch,
    permissions.grounding,
    eventFilters.severity,
    filterGroundingEvents,
    filterGroundingEventsBySeverity,
  ]);

  // Combine and sort both types of events
  const combinedEvents = useMemo(() => {
    const merged = mergeSortEvents(
      filteredRegularEvents,
      filteredGroundingEvents
    );
    return sortEvents(merged);
  }, [filteredRegularEvents, filteredGroundingEvents, sortEvents]);

  return {
    filteredEvents: filteredRegularEvents,
    filteredGroundingEvents,
    combinedEvents,
    eventFilters,
    setEventFilters,
    sortConfig,
    setSortConfig,
  };
};

const mergeSortEvents = (
  events: DetectedEvent[],
  groundingEvents: CombinedGroundingEventAndConfiguration[]
) => {
  const mergedEvents = [
    ...events.map((event) => ({
      event,
      id: event.id,
      timestamp: event.activated, // Use activated field for regular events
      type: 'regular',
    })),
    ...groundingEvents.map((event) => ({
      event,
      id: event.event.id,
      timestamp: event.event.groundingTime,
      type: 'grounding',
    })),
  ];

  return mergedEvents;
};
